import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Upload, Trash2, FileText, Download } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function DocumentsManager() {
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(false);

  const fetchDocs = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/admin/documents`, { withCredentials: true });
      setDocs(res.data);
    } catch { setDocs([]); }
  }, []);

  useEffect(() => { fetchDocs(); }, [fetchDocs]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      await axios.post(`${API}/api/admin/documents`, formData, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      fetchDocs();
    } catch (err) {
      alert('Upload failed: ' + (err.response?.data?.detail || err.message));
    } finally { setUploading(false); e.target.value = ''; }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this document?')) return;
    await axios.delete(`${API}/api/admin/documents/${id}`, { withCredentials: true });
    fetchDocs();
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / 1048576).toFixed(1) + ' MB';
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-white">Documents</h1>
        <label className="bg-[#F2B705] text-black font-bold text-xs uppercase px-5 py-2.5 hover:bg-[#D9A404] flex items-center gap-2 cursor-pointer">
          <Upload size={14} /> {uploading ? 'Uploading...' : 'Upload'}
          <input type="file" accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx" onChange={handleUpload} className="hidden" />
        </label>
      </div>

      <div className="space-y-3">
        {docs.map(doc => (
          <div key={doc.id} className="bg-[#161616] border border-white/10 p-5 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <FileText size={18} className="text-[#F2B705] shrink-0" />
              <div className="min-w-0">
                <p className="text-sm text-white truncate">{doc.original_name}</p>
                <p className="text-xs text-[#666]">{formatSize(doc.size)} &middot; {new Date(doc.uploaded_at).toLocaleDateString()}</p>
              </div>
            </div>
            <div className="flex gap-2 shrink-0">
              <a href={`${API}${doc.url}`} target="_blank" rel="noopener noreferrer" className="text-[#888] hover:text-[#F2B705] p-2"><Download size={14} /></a>
              <button onClick={() => handleDelete(doc.id)} className="text-[#888] hover:text-red-400 p-2"><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
        {docs.length === 0 && <p className="text-[#666] text-sm">No documents uploaded. Click "Upload" to add capability statements, brochures, or other PDFs.</p>}
      </div>
    </div>
  );
}
